// react
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { css } from 'styled-components';

// components
import { Button } from '@/components/atoms/Button/Button';
import { LoadingTransition } from '@/components/atoms/LoadingTransition/LoadingTransition';
import { Skeleton } from '@/components/atoms/Skeleton/Skeleton';

// assets
// @transform-path ../../../../../../src/assets/keni/call.svg
import keniCall from '@/assets/keni/call.svg';

/**
 * CallToAction displays a call to action.
 */
export const CallToAction = ({
  title,
  text,
  action,
  onPress,
  isLoading = true,
  isMobile = false,
}: CallToActionProps) => {
  const [isLoaded, setIsLoaded] = useState(false);

  // effects
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      setIsLoaded(true);
    };
    img.src = keniCall;
  }, []);
  return isMobile ? (
    <MobileWrapper>
      <Content>
        <Title $isMobile>{title}</Title>
        <MobileText>
          <Text $isMobile>{text}</Text>
        </MobileText>
      </Content>
      <Button onPress={onPress} ariaLabel='book a call'>
        {action}
      </Button>
    </MobileWrapper>
  ) : (
    <Wrapper>
      <LoadingTransition
        isLoading={isLoading || !isLoaded}
        loading={
          <>
            <Skeleton shape='avatar' />
            <Skeleton shape='subtitle' />
            <Skeleton shape='paragraph' />
            <Skeleton shape='button' isMargin={false} />
          </>
        }
        content={
          <>
            <Content>
              <KeniCall>
                <img src={keniCall} alt='Keni Call' />
              </KeniCall>
              <Title>{title}</Title>
              <Text>{text}</Text>
            </Content>
            <Button onPress={onPress} ariaLabel='book a call'>
              {action}
            </Button>
          </>
        }
      />
    </Wrapper>
  );
};

interface MobileProps {
  $isMobile?: boolean;
}

const MobileWrapper = styled.div`
  position: fixed;
  bottom: 50px;
  left: 0;
  width: 100%;
  display: flex;
  background: ${(props) => props.theme.color.neutral[10]};
  gap: ${(props) => props.theme.spacing[24]};
  justify-content: space-between;
  border: 1px solid ${(props) => props.theme.color.neutral[40]};
  border-bottom: none;
  align-items: center;
  box-shadow: ${(props) => props.theme.elevation[3]};
  padding: ${(props) => props.theme.spacing[36]};
  padding-top: ${(props) => props.theme.spacing[20]};
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;

  flex-direction: row;
  ${(props) =>
    props.theme.breakpoint.sm(css`
      flex-direction: row;
      bottom: 0;
    `)};
`;

const MobileText = styled.div`
  display: none;
  ${(props) =>
    props.theme.breakpoint.sm(css`
      display: block;
    `)};
`;

const Wrapper = styled.div`
  position: sticky;
  top: 0;
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[24]};
  align-items: center;
  min-width: 300px;
  width: 300px;
  max-width: 300px;
  border: 1px solid ${(props) => props.theme.color.neutral[40]};
  box-shadow: ${(props) => props.theme.elevation[3]};
  padding: ${(props) => props.theme.spacing[36]};
  padding-top: ${(props) => props.theme.spacing[20]};
  border-radius: 8px;
`;

const Content = styled.div``;

const KeniCall = styled.div`
  display: flex;
  justify-content: center;
`;

const Title = styled.div<MobileProps>`
  font: ${(props) => props.theme.typography.body.md.semibold};
  color: ${(props) => props.theme.color.neutral[90]};
  text-align: ${(props) => (props.$isMobile ? 'left' : 'center')};
`;

const Text = styled.div<MobileProps>`
  font: ${(props) => props.theme.typography.body.md.regular};
  color: ${(props) => props.theme.color.neutral[90]};
  text-align: ${(props) => (props.$isMobile ? 'left' : 'center')};
`;

interface CallToActionProps {
  title: string;
  text: string;
  action: string;
  onPress?: () => void;
  isLoading?: boolean;
  isMobile?: boolean;
}

CallToAction.displayName = 'CallToAction';

export default CallToAction;
